/* eslint-disable @typescript-eslint/restrict-template-expressions */
import {
  EventAvailable,
  HourglassBottom,
  HourglassEmpty,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Fragment, memo, useState } from 'react';

import { CountryPeopleListItem, CountryPetsListItem, DiseaseRefWithCompleted, Pagination } from 'shared/api';

import { formatCheckedDate } from '../../lib/country-attachments';

type CountryListItem = CountryPeopleListItem | CountryPetsListItem;

type Props = {
  list: CountryListItem[];
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
  loadingDiseaseId?: string | null;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string) => void;
  onDiseaseClick?: (diseaseId: string, countryCode: string) => void;
  onCountryInfoClick?: (countryCode: string) => void;
};

export const CountriesTablePaginated = memo(
  ({
    list,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading = false,
    loadingDiseaseId = null,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    onDiseaseClick,
    onCountryInfoClick,
  }: Props) => {
    const handleChangePage = (event: unknown, newPage: number) => {
      onPageChange(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    const handleSortRequest = (columnId: string) => {
      onSortChange(columnId);
    };

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 305px)' }}>
            <Table stickyHeader aria-label="countries table">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'countryName'}
                      direction={sortBy === 'countryName' ? sortOrder : 'asc'}
                      onClick={() => handleSortRequest('countryName')}
                    >
                      Country
                    </TableSortLabel>
                  </TableCell>
                  {/* Статус паспорта и кнопка его редактирования — одна колонка.
                      Сортировка та же, что была у отдельной колонки с датой */}
                  <TableCell sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={sortBy === 'lastCheckedAt'}
                      direction={sortBy === 'lastCheckedAt' ? sortOrder : 'asc'}
                      onClick={() => handleSortRequest('lastCheckedAt')}
                    >
                      National calendar
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Mandatory</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Basic</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Additional</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Epidemic</TableCell>
                  <TableCell>Not distributed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length > 0 ? (
                  list.map((row) => (
                    <Row
                      key={row.countryCode}
                      row={row}
                      loadingDiseaseId={loadingDiseaseId}
                      onDiseaseClick={onDiseaseClick}
                      onCountryInfoClick={onCountryInfoClick}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No countries found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {pagination && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

CountriesTablePaginated.displayName = 'CountriesTablePaginated';

type RowProps = {
  row: CountryListItem;
  loadingDiseaseId?: string | null;
  onDiseaseClick?: (diseaseId: string, countryCode: string) => void;
  onCountryInfoClick?: (countryCode: string) => void;
};

const Row = ({ row, loadingDiseaseId = null, onDiseaseClick, onCountryInfoClick }: RowProps) => {
  const [open, setOpen] = useState(false);

  const getDiseaseBox = (disease: DiseaseRefWithCompleted): JSX.Element => {
    const isLoading = loadingDiseaseId === disease.id;
    return (
      <Box
        key={`${row.countryCode}_${disease.id}`}
        component="span"
        p={0.5}
        m={0.25}
        onClick={() => {
          if (!isLoading) {
            onDiseaseClick?.(disease.id, row.countryCode);
          }
        }}
        sx={{
          position: 'relative',
          backgroundColor: disease.completed ? '#e6f7f4' : 'rgba(0, 0, 0, 0.07)',
          borderRadius: '4px',
          color: disease.verified ? '#29b194' : 'inherit',
          cursor: isLoading ? 'default' : onDiseaseClick ? 'pointer' : 'default',
          '&:hover':
            onDiseaseClick && !isLoading
              ? {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                }
              : {},
        }}
      >
        {isLoading && (
          <CircularProgress
            size={14}
            sx={{
              color: '#29b194',
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-7px',
              marginLeft: '-7px',
            }}
          />
        )}
        <Box component="span" sx={{ opacity: isLoading ? 0.3 : 1, transition: 'opacity 0.2s' }}>
          {disease.name_en}
        </Box>
      </Box>
    );
  };

  /**
   * Три состояния паспорта — ровно те, что различимы по данным бека, без вычислений на фронте.
   *
   * needsCheck приходит с бека и означает «срок УЖЕ вышел» (или проверки не было вовсе),
   * а не «скоро выйдет»: понятия «осталось немного» бек не отдаёт ни флагом, ни датой окончания.
   * Поэтому четвёртого состояния здесь нет — оно появится, когда бек начнёт отдавать dueAt.
   */
  const needsCheck = row.info?.needsCheck ?? true;
  const lastCheckedAt = row.info?.lastCheckedAt;

  const calendarStatus = !needsCheck
    ? {
        icon: <EventAvailable fontSize="small" />,
        color: 'success.main',
        tooltip: `Last checked ${formatCheckedDate(lastCheckedAt)}`,
      }
    : lastCheckedAt
    ? {
        icon: <HourglassBottom fontSize="small" />,
        // error.dark, а не error.main: тема переопределяет main на ярко-розовый #FF1744,
        // который выбивается из палитры и даёт всего 3.85:1
        color: 'error.dark',
        tooltip: `Check overdue — last checked ${formatCheckedDate(lastCheckedAt)}`,
      }
    : {
        icon: <HourglassEmpty fontSize="small" />,
        color: 'warning.dark',
        tooltip: 'Never checked',
      };

  return (
    <Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)} sx={{ color: '#29b194' }}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{row.countryName}</TableCell>
        {/* position: relative обязателен: visuallyHidden — это position: absolute, и без
            позиционированной ячейки его containing block — сам документ. Тогда span встаёт
            на высоту своей строки в 17k-px таблице, скролл-контейнер его не обрезает,
            и у всего окна появляется скролл с пустотой под таблицей. */}
        <TableCell sx={{ whiteSpace: 'nowrap', position: 'relative' }}>
          {/* Иконка одновременно показывает статус и открывает редактирование.
              Цвет не единственный носитель смысла: у каждого состояния своя форма иконки,
              формулировка — в тултипе, а для скринридера статус лежит в самой ячейке
              скрытым текстом (иначе колонка озвучивалась бы как пустая) */}
          <Box component="span" sx={visuallyHidden}>
            {calendarStatus.tooltip}
          </Box>
          <Tooltip
            title={
              <>
                {calendarStatus.tooltip}
                <Box component="span" sx={{ display: 'block', opacity: 0.8 }}>
                  Click to edit
                </Box>
              </>
            }
          >
            <IconButton
              aria-label="edit country info"
              size="small"
              onClick={() => onCountryInfoClick?.(row.countryCode)}
              sx={{ color: calendarStatus.color }}
            >
              {calendarStatus.icon}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', verticalAlign: 'top' }}>
            {row.Mandatory.map((item) => getDiseaseBox(item))}
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', verticalAlign: 'top' }}>
            {row.Basic.map((item) => getDiseaseBox(item))}
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', verticalAlign: 'top' }}>
            {row.Additional.map((item) => getDiseaseBox(item))}
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', verticalAlign: 'top' }}>
            {row.Epidemic.map((item) => getDiseaseBox(item))}
          </Box>
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          <Box component="span" p={0.5} m={0.25} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', borderRadius: '4px' }}>
            {row.NotDistributed.length}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {open && (
              <Box sx={{ margin: 1 }}>
                <Table size="small" aria-label="not-distributed">
                  <TableHead>
                    <TableRow>
                      <TableCell>Not distributed diseases</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {/* Ячейка обязательна: div прямо внутри tr — невалидный HTML, React ругается validateDOMNesting */}
                      <TableCell sx={{ borderBottom: 'unset', p: 0 }}>
                        <Box px={2} my={0.5} display="flex" flexWrap={'wrap'}>
                          {row.NotDistributed.length > 0 ? (
                            row.NotDistributed.map((disease) => (
                              <Box
                                key={disease.id}
                                component="span"
                                p={0.5}
                                m={0.25}
                                onClick={() => onDiseaseClick?.(disease.id, row.countryCode)}
                                sx={{
                                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                  borderRadius: '4px',
                                  cursor: onDiseaseClick ? 'pointer' : 'default',
                                  '&:hover': onDiseaseClick
                                    ? {
                                        backgroundColor: 'rgba(41, 177, 148, 0.2)',
                                        transition: 'background-color 0.2s',
                                      }
                                    : {},
                                }}
                              >
                                {disease.name_en}
                              </Box>
                            ))
                          ) : (
                            <Box sx={{ color: 'rgba(116, 116, 116, 1)' }}>All diseases distributed</Box>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};
